import { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Eye, Plus, Edit, Trash2, ArrowLeft, FileText, X, FileImage, File, ChevronLeft, ChevronRight } from "lucide-react";
import Masonry from "react-responsive-masonry";

export function ResourcesTab() {
	const [user, setUser] = useState(null);
	const [resources, setResources] = useState([]);
	const [loading, setLoading] = useState(true);
	const [view, setView] = useState("list");
	const [selectedResource, setSelectedResource] = useState(null);
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [editingResource, setEditingResource] = useState(null);
	const [deletingResourceId, setDeletingResourceId] = useState(null);

	// Image viewer state
	const [imageViewerOpen, setImageViewerOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	// Form state
	const [formTitle, setFormTitle] = useState("");
	const [formDescription, setFormDescription] = useState("");
	const [formType, setFormType] = useState("document");
	const [formDocumentUrl, setFormDocumentUrl] = useState("");
	const [formImageUrls, setFormImageUrls] = useState([""]);
	const [formThumbnailUrl, setFormThumbnailUrl] = useState("");

	// Check authentication
	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
			setUser(currentUser);
		});
		return () => unsubscribe();
	}, []);

	// Fetch resources from Firestore
	useEffect(() => {
		fetchResources();
	}, []);

	const fetchResources = async () => {
		try {
			setLoading(true);
			const querySnapshot = await getDocs(collection(db, "resources"));
			const resourcesData = querySnapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));
			setResources(resourcesData);
		} catch (error) {
			console.error("Error fetching resources:", error);
			setResources([]);
		} finally {
			setLoading(false);
		}
	};

	const handleViewResource = (resource) => {
		if (resource.type === "gallery" && resource.images) {
			setSelectedResource(resource);
			setView("gallery");
		} else if (resource.type === "document" && resource.documentUrl) {
			// Open document in new tab
			window.open(resource.documentUrl, "_blank");
		}
	};

	const handleImageClick = (index) => {
		setSelectedImageIndex(index);
		setImageViewerOpen(true);
	};

	const handlePreviousImage = () => {
		if (selectedResource && selectedResource.images) {
			setSelectedImageIndex((prev) =>
				prev === 0 ? selectedResource.images.length - 1 : prev - 1
			);
		}
	};

	const handleNextImage = () => {
		if (selectedResource && selectedResource.images) {
			setSelectedImageIndex((prev) =>
				prev === selectedResource.images.length - 1 ? 0 : prev + 1
			);
		}
	};

	const handleBackToList = () => {
		setView("list");
		setSelectedResource(null);
	};

	const resetForm = () => {
		setFormTitle("");
		setFormDescription("");
		setFormType("document");
		setFormDocumentUrl("");
		setFormThumbnailUrl("");
		setFormImageUrls([""]);
		setEditingResource(null);
	};

	const handleAddResource = async () => {
		if (!isFormValid()) return;

		try {
			const newResource = {
				title: formTitle,
				description: formDescription,
				type: formType,
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp()
			};

			if (formType === "document") {
				newResource.documentUrl = formDocumentUrl;
				if (formThumbnailUrl.trim()) newResource.thumbnailUrl = formThumbnailUrl.trim();
			} else {
				newResource.images = formImageUrls.filter((url) => url.trim() !== "");
			}

			await addDoc(collection(db, "resources"), newResource);
			alert("Resource added successfully!");
			resetForm();
			setIsAddDialogOpen(false);
			fetchResources();
		} catch (error) {
			console.error("Error adding resource:", error);
			alert("Failed to add resource: " + error.message);
		}
	};

	const handleEditResource = async () => {
		if (!editingResource || !isFormValid()) return;

		try {
			const resourceRef = doc(db, "resources", editingResource.id);
			const updatedResource = {
				title: formTitle,
				description: formDescription,
				type: formType,
				updatedAt: serverTimestamp()
			};

			if (formType === "document") {
				updatedResource.documentUrl = formDocumentUrl;
				updatedResource.thumbnailUrl = formThumbnailUrl.trim() || null;
			} else {
				updatedResource.images = formImageUrls.filter((url) => url.trim() !== "");
			}

			await updateDoc(resourceRef, updatedResource);
			alert("Resource updated successfully!");
			resetForm();
			setIsEditDialogOpen(false);
			fetchResources();
		} catch (error) {
			console.error("Error updating resource:", error);
			alert("Failed to update resource: " + error.message);
		}
	};

	const handleDeleteResource = async () => {
		if (!deletingResourceId) return;

		try {
			await deleteDoc(doc(db, "resources", deletingResourceId));
			alert("Resource deleted successfully!");
			setDeletingResourceId(null);
			setIsDeleteDialogOpen(false);
			fetchResources();
		} catch (error) {
			console.error("Error deleting resource:", error);
			alert("Failed to delete resource");
		}
	};

	const openEditDialog = (resource) => {
		setEditingResource(resource);
		setFormTitle(resource.title);
		setFormDescription(resource.description);
		setFormType(resource.type);

		if (resource.type === "document") {
			setFormDocumentUrl(resource.documentUrl || "");
			setFormThumbnailUrl(resource.thumbnailUrl || "");
			setFormImageUrls([""]);
		} else {
			setFormDocumentUrl("");
			setFormImageUrls(
				resource.images && resource.images.length > 0
					? resource.images
					: [""]
			);
		}

		setIsEditDialogOpen(true);
	};

	const addImageUrlField = () => {
		setFormImageUrls([...formImageUrls, ""]);
	};

	const removeImageUrlField = (index) => {
		setFormImageUrls(formImageUrls.filter((_, i) => i !== index));
	};

	const updateImageUrl = (index, value) => {
		const newUrls = [...formImageUrls];
		newUrls[index] = value;
		setFormImageUrls(newUrls);
	};

	const isFormValid = () => {
		if (!formTitle.trim() || !formDescription.trim()) return false;

		if (formType === "document") {
			return formDocumentUrl.trim() !== "";
		} else {
			return formImageUrls.some((url) => url.trim() !== "");
		}
	};

	const openDeleteDialog = (resourceId) => {
		setDeletingResourceId(resourceId);
		setIsDeleteDialogOpen(true);
	};

	// Gallery View
	if (view === "gallery" && selectedResource) {
		return (
			<div className="space-y-6">
				<Button onClick={handleBackToList} variant="secondary" className="inline-flex items-center gap-2">
					<ArrowLeft className="h-4 w-4" />
					Back to Resources
				</Button>

				<div>
					<h1 className="text-4xl font-bold text-white mb-2">{selectedResource.title}</h1>
					<p className="text-gray-300 mt-1">
						{selectedResource.description}
					</p>
				</div>

				<div className="mt-6">
					<Masonry columnsCount={3} gutter="16px">
						{selectedResource.images?.map((image, index) => (
							<div
								key={index}
								className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
								onClick={() => handleImageClick(index)}
							>
								<img
									src={image}
									alt={`${selectedResource.title} - Image ${index + 1}`}
									className="w-full h-auto object-cover hover:scale-105 transition-transform duration-300"
									onError={(e) => {
										e.currentTarget.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
									}}
								/>
							</div>
						))}
					</Masonry>
				</div>

				{/* Full-Screen Image Viewer */}
				<Dialog open={imageViewerOpen} onOpenChange={setImageViewerOpen}>
					<DialogContent className="!fixed !inset-0 !w-screen !h-screen !p-0 !m-0 !overflow-hidden !bg-black !max-w-none !rounded-none !border-none !shadow-none !top-0 !left-0 !translate-x-0 !translate-y-0">
						{/* Clickable background to close */}
						<div
							className="absolute inset-0 z-10 cursor-pointer"
							onClick={() => setImageViewerOpen(false)}
						/>

						<div
							className="relative w-full h-full flex items-center justify-center z-20 pointer-events-none"
						>
							{/* Previous Button - Left Edge */}
							<Button
								variant="secondary"
								size="sm"
								className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 pointer-events-auto"
								onClick={handlePreviousImage}
							>
								<ChevronLeft className="h-8 w-8" />
							</Button>

							{/* Main Image Container */}
							<div className="absolute inset-0 flex items-center justify-center px-20 py-20">
								<img
									src={selectedResource.images?.[selectedImageIndex]}
									alt={`${selectedResource.title} - Image ${selectedImageIndex + 1}`}
									className="pointer-events-auto"
									style={{
										maxWidth: '100%',
										maxHeight: '100%',
										width: 'auto',
										height: 'auto',
										objectFit: 'contain'
									}}
									onError={(e) => {
										e.currentTarget.src = "https://via.placeholder.com/1200x800?text=Image+Not+Found";
									}}
								/>
							</div>

							{/* Next Button - Right Edge */}
							<Button
								variant="secondary"
								size="sm"
								className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 pointer-events-auto"
								onClick={handleNextImage}
							>
								<ChevronRight className="h-8 w-8" />
							</Button>

							{/* Image Counter - Bottom Center */}
							<div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 bg-black/70 text-white px-6 py-3 rounded-full text-sm font-medium pointer-events-none">
								{selectedImageIndex + 1} / {selectedResource.images?.length}
							</div>
						</div>
					</DialogContent>
				</Dialog>
			</div>
		);
	}

	// List View
	if (loading) {
		return (
			<div className="section-container">
				<div className="section-card text-center py-8">
					<div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
					<p className="mt-4 text-gray-300">Loading resources...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="section-container">
			{/* Header Card */}
			<div className="section-card">
				<div className="flex items-center justify-between">
					<div>
						<h3 className="section-subtitle">Resources</h3>
						<p className="text-muted-foreground text-sm mt-1">
							Access project documents and photo galleries
						</p>
					</div>
					{user && (
						<Button
							onClick={() => setIsAddDialogOpen(true)}
							className="btn-primary inline-flex items-center gap-2"
						>
							<Plus className="h-4 w-4" />
							Add Resource
						</Button>
					)}
				</div>
			</div>

			{/* Loading State */}
			{loading ? (
				<div className="section-card text-center py-8">
					<div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
					<p className="mt-4 text-gray-300">Loading resources...</p>
				</div>
			) : (
				<>
					{/* Resources Grid */}
					<div className="section-card">
						{resources.length > 0 ? (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
								{resources.map((resource) => (
									<div key={resource.id} className="group">
										{/* Image Preview for Galleries */}
										{resource.type === "gallery" && resource.images && resource.images.length > 0 ? (
											<div className="relative h-40 overflow-hidden rounded-lg mb-4 bg-gray-900">
												<img
													src={resource.images[0]}
													alt={resource.title}
													className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
													onError={(e) => {
														e.currentTarget.style.display = 'none';
													}}
												/>
												{resource.images.length > 1 && (
													<div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
														+{resource.images.length - 1}
													</div>
												)}
											</div>
										) : (
											<div className="relative h-40 overflow-hidden rounded-lg mb-4 bg-gray-900 group-hover:opacity-90 transition-opacity">
												{resource.type === "document" && resource.thumbnailUrl ? (
													<img
														src={resource.thumbnailUrl}
														alt={resource.title}
														className="w-full h-full object-cover"
														onError={(e) => {
															e.currentTarget.style.display = 'none';
															e.currentTarget.parentElement.classList.add('flex', 'items-center', 'justify-center');
															const icon = document.createElement('div');
															icon.innerHTML = '<svg class="h-16 w-16 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>';
															e.currentTarget.parentElement.appendChild(icon);
														}}
													/>
												) : (
													<div className="flex items-center justify-center h-full w-full">
														<File className="h-16 w-16 text-blue-600 dark:text-blue-400" />
													</div>
												)}
											</div>
										)}


										{/* Resource Content */}
										<div className="flex items-start justify-between gap-2 mb-2">
											<div className="flex items-start gap-2 flex-1">
												{resource.type === "gallery" ? (
													<FileImage className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
												) : (
													<File className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
												)}
												<h3 className="text-base font-semibold text-white group-hover:text-secondary transition-colors">
													{resource.title}
												</h3>
											</div>
											{user && (
												<div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
													<button
														onClick={() => openEditDialog(resource)}
														className="p-1.5 hover:bg-gray-700 rounded transition-colors text-gray-400 hover:text-gray-200"
														title="Edit resource"
													>
														<Edit className="h-4 w-4" />
													</button>
													<button
														onClick={() => openDeleteDialog(resource.id)}
														className="p-1.5 hover:bg-red-600/20 rounded transition-colors text-red-400 hover:text-red-300"
														title="Delete resource"
													>
														<Trash2 className="h-4 w-4" />
													</button>
												</div>
											)}
										</div>

										<p className="text-sm text-gray-300 mb-4 leading-relaxed">
											{resource.description}
										</p>

										<button
											onClick={() => handleViewResource(resource)}
											className="w-full px-4 py-2 rounded-lg text-white bg-gray-700 hover:bg-gray-600 transition-colors text-sm font-medium inline-flex items-center justify-center gap-2"
										>
											<Eye className="h-4 w-4" />
											View
										</button>
									</div>
								))}
							</div>
						) : (
							<div className="text-center py-12">
								<FileImage className="h-12 w-12 text-gray-600 mx-auto mb-3" />
								<h3 className="text-lg font-semibold text-gray-300 mb-2">No Resources Yet</h3>
								<p className="text-gray-400 mb-6">Resources will appear here when added.</p>
								{user && (
									<Button
										onClick={() => setIsAddDialogOpen(true)}
										className="btn-primary inline-flex items-center gap-2"
									>
										<Plus className="h-4 w-4" />
										Create First Resource
									</Button>
								)}
							</div>
						)}
					</div>
				</>
			)}

			{/* Add Resource Dialog */}
			{user && (
				<Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
					<DialogContent className="max-h-[90vh] overflow-y-auto">
						<DialogHeader>
							<DialogTitle>Add New Resource</DialogTitle>
							<DialogDescription>
								Create a new resource document or gallery for the library
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="add-title">Title</Label>
								<Input
									id="add-title"
									value={formTitle}
									onChange={(e) => setFormTitle(e.target.value)}
									placeholder="Enter resource title"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="add-description">Description</Label>
								<Textarea
									id="add-description"
									value={formDescription}
									onChange={(e) => setFormDescription(e.target.value)}
									placeholder="Enter resource description"
									rows={3}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="add-resource-type">Resource Type</Label>
								<Select
									value={formType}
									onValueChange={(value) => setFormType(value)}
								>
									<SelectTrigger id="add-resource-type">
										<SelectValue placeholder="Select resource type" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="document">
											<div className="flex items-center gap-2">
												<File className="h-4 w-4" />
												<span>Document</span>
											</div>
										</SelectItem>
										<SelectItem value="gallery">
											<div className="flex items-center gap-2">
												<FileImage className="h-4 w-4" />
												<span>Gallery</span>
											</div>
										</SelectItem>
									</SelectContent>
								</Select>
							</div>

							{formType === "document" ? (
								<>
									<div className="space-y-2">
										<Label htmlFor="add-document-url">Document URL</Label>
										<Input
											id="add-document-url"
											value={formDocumentUrl}
											onChange={(e) => setFormDocumentUrl(e.target.value)}
											placeholder="https://example.com/document.pdf"
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="add-thumbnail-url">Thumbnail Image URL (Optional)</Label>
										<Input
											id="add-thumbnail-url"
											value={formThumbnailUrl}
											onChange={(e) => setFormThumbnailUrl(e.target.value)}
											placeholder="https://example.com/image.jpg"
										/>
									</div>
								</>
							) : (
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<Label>Image URLs</Label>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addImageUrlField}
										>
											<Plus className="h-4 w-4 mr-1" />
											Add Image
										</Button>
									</div>
									<div className="space-y-2 max-h-60 overflow-y-auto">
										{formImageUrls.map((url, index) => (
											<div key={index} className="flex gap-2">
												<Input
													value={url}
													onChange={(e) => updateImageUrl(index, e.target.value)}
													placeholder={`Image URL ${index + 1}`}
												/>
												{formImageUrls.length > 1 && (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeImageUrlField(index)}
														className="flex-shrink-0"
													>
														<X className="h-4 w-4" />
													</Button>
												)}
											</div>
										))}
									</div>
								</div>
							)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setIsAddDialogOpen(false);
									resetForm();
								}}
							>
								Cancel
							</Button>
							<Button onClick={handleAddResource} disabled={!isFormValid()}>
								Add Resource
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}

			{/* Edit Resource Dialog */}
			{user && (
				<Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
					<DialogContent className="max-h-[90vh] overflow-y-auto">
						<DialogHeader>
							<DialogTitle>Edit Resource</DialogTitle>
							<DialogDescription>
								Update the resource information
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="edit-title">Title</Label>
								<Input
									id="edit-title"
									value={formTitle}
									onChange={(e) => setFormTitle(e.target.value)}
									placeholder="Enter resource title"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="edit-description">Description</Label>
								<Textarea
									id="edit-description"
									value={formDescription}
									onChange={(e) => setFormDescription(e.target.value)}
									placeholder="Enter resource description"
									rows={3}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="edit-resource-type">Resource Type</Label>
								<Select
									value={formType}
									onValueChange={(value) => setFormType(value)}
								>
									<SelectTrigger id="edit-resource-type">
										<SelectValue placeholder="Select resource type" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="document">
											<div className="flex items-center gap-2">
												<File className="h-4 w-4" />
												<span>Document</span>
											</div>
										</SelectItem>
										<SelectItem value="gallery">
											<div className="flex items-center gap-2">
												<FileImage className="h-4 w-4" />
												<span>Gallery</span>
											</div>
										</SelectItem>
									</SelectContent>
								</Select>
							</div>

							{formType === "document" ? (
								<>
									<div className="space-y-2">
										<Label htmlFor="edit-document-url">Document URL</Label>
										<Input
											id="edit-document-url"
											value={formDocumentUrl}
											onChange={(e) => setFormDocumentUrl(e.target.value)}
											placeholder="https://example.com/document.pdf"
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="edit-thumbnail-url">Thumbnail Image URL (Optional)</Label>
										<Input
											id="edit-thumbnail-url"
											value={formThumbnailUrl}
											onChange={(e) => setFormThumbnailUrl(e.target.value)}
											placeholder="https://example.com/image.jpg"
										/>
									</div>
								</>
							) : (
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<Label>Image URLs</Label>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={addImageUrlField}
										>
											<Plus className="h-4 w-4 mr-1" />
											Add Image
										</Button>
									</div>
									<div className="space-y-2 max-h-60 overflow-y-auto">
										{formImageUrls.map((url, index) => (
											<div key={index} className="flex gap-2">
												<Input
													value={url}
													onChange={(e) => updateImageUrl(index, e.target.value)}
													placeholder={`Image URL ${index + 1}`}
												/>
												{formImageUrls.length > 1 && (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => removeImageUrlField(index)}
														className="flex-shrink-0"
													>
														<X className="h-4 w-4" />
													</Button>
												)}
											</div>
										))}
									</div>
								</div>
							)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setIsEditDialogOpen(false);
									resetForm();
								}}
							>
								Cancel
							</Button>
							<Button onClick={handleEditResource} disabled={!isFormValid()}>
								Save Changes
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}

			{/* Delete Confirmation Dialog */}
			{user && (
				<AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Are you sure?</AlertDialogTitle>
							<AlertDialogDescription>
								This action cannot be undone. This will permanently delete the resource
								from the library.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={handleDeleteResource}
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							>
								Delete
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			)}
		</div>
	);
}
